import React, { useState, useEffect, useRef, type FC } from "react";
import type { ErrorReason } from "../../../core/services/fileUploadService";
import FilePreview, { FileUpload } from "../../../components/FilePreview";
import ArrowRightIcon from "../../../components/icons/ArrowRightIcon";
import AttachmentIcon from "../../../components/icons/AttachmentIcon";
import VoiceModeIcon from "../../../components/icons/VoiceModeIcon";
import { useTranslation } from "../../../contexts/useTranslation";
import { useWidget } from "../../../contexts/useWidget";
import { TypingState } from "../../../core/channels/messageThreadChannel";
import { useCore } from "../../../contexts/useCore";
import { Slot, Templated } from "../../../templating";

interface ChatFormTextProps {
  connected: boolean;
  onStartVoiceMode: () => void;
}

const initialFileUpload: FileUpload = {
  file: null,
  id: null,
  error: null,
  progress: 0
};

const TEXTAREA_HEIGHT = { min: 54, max: 220 };

const ChatFormText: FC<ChatFormTextProps> = ({ connected, onStartVoiceMode }) => {
  const { translate } = useTranslation();
  const { voiceModeAvailable, attachmentsEnabled } = useWidget();
  const core = useCore();
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [userInput, setUserInput] = useState("");
  const [fileUpload, setFileUpload] = useState<FileUpload>(initialFileUpload);
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const uploadAbortRef = useRef<AbortController | null>(null);
  const typingTimeoutRef = useRef<number | null>(null);

  const resetFileUpload = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (uploadAbortRef.current) {
      uploadAbortRef.current.abort();
      uploadAbortRef.current = null;
    }
    setFileUpload(initialFileUpload);
  };

  const clearTypingTimeout = (): void => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  };

  const reset = (): void => {
    resetFileUpload();
    setUserInput("");
    clearTypingTimeout();
    core._sendTypingNotification(TypingState.NOT_TYPING);
  };

  const resizeTextarea = (): void => {
    if (textareaRef.current) {
      // Resets the height to shrink if necessary
      textareaRef.current.style.height = `${TEXTAREA_HEIGHT.min}px`;

      // Adjusts the height according to the scroll height
      const newHeight = Math.min(textareaRef.current.scrollHeight, TEXTAREA_HEIGHT.max);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  };

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void core.sendMessage(userInput, { attachmentId: fileUpload.id ?? undefined });
    reset();
  };

  const handleTextareaPaste = (event: React.ClipboardEvent<HTMLTextAreaElement>): void => {
    // With attachments off, a pasted file is ignored; text pastes as usual.
    if (!attachmentsEnabled) return;
    const item = event.clipboardData.items[0]; // Takes only the first clipboard item
    if (item && item.kind === "file") {
      event.preventDefault();
      setFileUpload({ ...initialFileUpload, file: item.getAsFile()! });
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    setFileUpload({ ...initialFileUpload, file: file ?? null });
  };

  const handleFileAttach = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleTextareaChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setUserInput(event.target.value);

    if (event.target.value.trim().length === 0) {
      return;
    }

    core._sendTypingNotification(TypingState.TYPING);
    clearTypingTimeout();

    typingTimeoutRef.current = window.setTimeout(() => {
      core._sendTypingNotification(TypingState.NOT_TYPING);
    }, 2000);
  };

  useEffect(() => {
    const handleTextareaKeyDown = (event: KeyboardEvent): void => {
      if (submitEnabled && event.key === "Enter" && !event.shiftKey) {
        event.preventDefault(); // Prevents the default behavior of adding a new line
        formRef.current?.requestSubmit();
      }
    };

    if (textareaRef.current) {
      textareaRef.current.addEventListener("keydown", handleTextareaKeyDown);
    }

    return () => {
      if (textareaRef.current) {
        textareaRef.current.removeEventListener("keydown", handleTextareaKeyDown);
      }
    };
  }, [submitEnabled]);

  // Add effect to handle resize when userInput changes
  useEffect(() => {
    resizeTextarea();
  }, [userInput]);

  useEffect(() => {
    const formFilled = userInput.trim().length > 0;
    setSubmitEnabled(formFilled && connected);
  }, [connected, userInput]);

  useEffect(() => {
    const getErrorMessage = (errorReason: ErrorReason): string => {
      switch (errorReason.error_type) {
        case "content_type_not_supported":
          return translate("content_type_not_supported");
        case "file_size_too_large":
          return translate("file_size_too_large", { max: errorReason.metadata.max });
        default:
          return translate("upload_failed_due_to_unknown_error");
      }
    };

    const uploadFile = async (file: File): Promise<void> => {
      const abort = new AbortController();
      uploadAbortRef.current = abort;
      try {
        const { id } = await core.attachFile(file, {
          signal: abort.signal,
          onProgress: (progress: number) => {
            setFileUpload(prevFileUpload => ({ ...prevFileUpload, progress }));
          },
        });
        uploadAbortRef.current = null;
        setFileUpload(prevFileUpload => ({ ...prevFileUpload, id }));
      } catch (err) {
        uploadAbortRef.current = null;
        if (abort.signal.aborted) return;
        const reason = (err as { cause?: { errorReason?: ErrorReason | null } })?.cause?.errorReason;
        resetFileUpload();
        if (reason) {
          setFileUpload({ ...initialFileUpload, error: getErrorMessage(reason) });
        }
      }
    };

    if (fileUpload.file) {
      void uploadFile(fileUpload.file);
    }

    return () => {
      // Aborts any ongoing upload when the component unmounts
      if (uploadAbortRef.current) {
        uploadAbortRef.current.abort();
        uploadAbortRef.current = null;
      }

      clearTypingTimeout();
    };
  }, [fileUpload.file]);

  return (
    <Templated id="lastbot-composer-text">
      <>
        {fileUpload.file && (
          <div className="pb-2">
            <Slot name="attachment-preview">
              <FilePreview
                fileUpload={fileUpload}
                onRemove={resetFileUpload}
              />
            </Slot>
          </div>
        )}
        <div className="rounded-[1.75rem] border border-lastbot-composer-border">
          <form
            ref={formRef}
            onSubmit={handleFormSubmit}
            className="relative"
          >
            {attachmentsEnabled && (
              <input
                type="file"
                ref={fileInputRef}
                tabIndex={-1}
                onChange={handleFileChange}
                className="hidden"
              />
            )}
            {/*
              Slot WRAPS the button (not vice versa) so embedder templates
              that replace lastbot-composer-text harvest a fully-wired
              <button onClick=…>. Putting the Slot inside the button would
              project only the inner <AttachmentIcon /> — embedders would
              get an icon they can't click. Same pattern as Navbar slots.
            */}
            {/* The account can switch visitor attachments off (LAS-3469); the
                server then refuses uploads, so the control is not offered.
                The slot is hidden so neither a container template's fallback
                nor a per-slot override can bring the control back (LAS-4711). */}
            {attachmentsEnabled ? (
              <Slot name="composer-attach">
                <button
                  type="button"
                  onClick={handleFileAttach}
                  className="absolute bottom-0 left-[7px] text-lastbot-icon-muted rounded-full p-2 mb-[7.5px] cursor-pointer"
                  data-testid={`chat-attach-file-button`}
                >
                  <AttachmentIcon />
                </button>
              </Slot>
            ) : (
              <Slot name="composer-attach" hidden />
            )}
            <Slot name="input">
              <textarea
                ref={textareaRef}
                value={userInput}
                onChange={handleTextareaChange}
                onPaste={handleTextareaPaste}
                className={`block w-full bg-transparent text-lastbot-on-surface resize-none h-[54px] pt-[14.5px] pb-3 ${attachmentsEnabled ? "px-14" : "ps-5 pe-14"} placeholder:text-lastbot-on-surface-subtle focus:outline-hidden md:scrollbar-hide`}
                autoFocus
                data-testid={`chat-input`}
              />
            </Slot>
            {!voiceModeAvailable || (fileUpload.file || userInput.trim().length > 0) ? (
              <Slot name="send-button">
                <button
                  type="submit"
                  disabled={!submitEnabled}
                  className="absolute bottom-0 right-[7px] bg-lastbot-send-surface text-lastbot-on-send-surface rounded-full p-2 mb-[7.5px] cursor-pointer disabled:text-lastbot-disabled-on-surface disabled:cursor-not-allowed disabled:bg-lastbot-disabled-surface"
                  data-testid={`chat-submit`}
                >
                  <ArrowRightIcon />
                </button>
              </Slot>
            ) : (
              <Slot name="composer-voice">
                <button
                  type="button"
                  onClick={onStartVoiceMode}
                  className="absolute bottom-0 right-[7px] bg-lastbot-send-surface text-lastbot-on-send-surface rounded-full p-2 mb-[7.5px] cursor-pointer"
                  data-testid={`chat-voice-mode`}
                >
                  <VoiceModeIcon />
                </button>
              </Slot>
            )}
          </form>
        </div>
        {fileUpload.error && <div className="px-6 pt-3 text-xs text-center text-lastbot-danger-strong" data-testid={`chat-file-upload-error`}>{fileUpload.error}</div>}
      </>
    </Templated>
  );
};

export default ChatFormText;
